// src/components/molecules/ChallengeSubmission/SubmissionCard.js

import React, { useState } from "react";
import {
  Avatar,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Link as MuiLink,
} from "@mui/material";
import ForwardRoundedIcon from "@mui/icons-material/ForwardRounded";
import { useTheme } from "styled-components";
import { Link } from "react-router-dom";

const SubmissionCard = React.memo(({ submission }) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false); // State to handle image error

  // Array of possible background colors from theme
  const backgroundColors = [
    theme.colors.green,
    theme.colors.amber,
    theme.colors.red,
  ];

  // Select a random color from the array
  const randomBackgroundColor =
    backgroundColors[Math.floor(Math.random() * backgroundColors.length)];

  // Function to handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        {/* Avatar */}
        <Avatar
          src={submission.profileData?.profileImage || ""}
          alt="Profile Image"
          sx={{
            width: 60,
            height: 60,
            marginRight: 2, // Space between avatar and text
          }}
        />
        {/* Title and Username */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontFamily: "basic-sans",
              fontWeight: 700,
              color: theme.colors.white,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: {
                xs: "1.3em", // Font size for mobile devices
                sm: "1em", // Font size for small tablets
                md: "1.1em", // Font size for tablets
                lg: "1.3em", // Font size for desktop
                xl: "1.5rem", // Font size for larger screens
              },
              maxWidth: {
                xs: "54vw", // Max width for mobile devices
                sm: "225px", // Max width for small tablets
                md: "16vw", // Max width for tablets
                lg: "230px", // Max width for desktop
                xl: "320px", // Max width for larger screens
              },
            }}
          >
            {submission.title || "Untitled submission"}
          </Typography>

          {submission.profileData?.userName && (
            <MuiLink
              href={`/challenge/profile/${submission.userId}`}
              underline="none"
              sx={{
                fontFamily: "basic-sans",
                fontSize: "1.2em",
                fontWeight: 500,
                color: randomBackgroundColor, // Use the random button color for userName
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {submission.profileData?.userName}
            </MuiLink>
          )}
        </Box>
      </Box>

      {/* Submission Card */}
      <Card
        sx={{
          maxWidth: 465,
          borderRadius: 5,
          position: "relative",
          mb: 4,
          backgroundColor: "transparent",
        }}
      >
        {/* Image Container with 16:9 Aspect Ratio */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9", // Maintains 16:9 aspect ratio
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
            overflow: "hidden",
            backgroundColor:
              imageError || !submission?.contentType
                ? randomBackgroundColor
                : "transparent",
          }}
        >
          {imageError || !submission?.contentType ? (
            // Fallback message if the image fails to load or contentType is missing
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 4,
                color: "white",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body1"
                component="p"
                sx={{
                  fontFamily: "basic-sans",
                  fontSize: "1.1em",
                  fontWeight: "normal",
                }}
              >
                {submission.status === "draft"
                  ? "This submission is not published."
                  : "This submission is being processed. Check back later."}
              </Typography>
            </Box>
          ) : (
            // CardMedia for the image, with error handling
            <CardMedia
              component="img"
              image={
                submission?.contentType.startsWith("video/")
                  ? `https://swayer.blob.core.windows.net/${submission?.submissionSaleId}-pub/thumbnail_master.png`
                  : submission?.contentType.startsWith("image/")
                  ? `https://swayer.blob.core.windows.net/${submission?.submissionSaleId}-pub/_thumb_large.jpg`
                  : ""
              }
              alt="Submission Cover"
              onError={handleImageError} // Handle image load error
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover", // Ensures the image covers the container
                objectPosition: "center", // Centers the image
              }}
            />
          )}
        </Box>

        <Link
          to={{
            pathname:
              submission.status === "draft"
                ? submission.challengeId
                  ? `/challenge/${submission.challengeId}/submission/${submission?.id}`
                  : `/upload-artwork`
                : submission.challengeId
                ? `/challenge/submission/${submission?.id}`
                : "/upload-artwork",
            search: !submission.challengeId
              ? `submissionId=${submission?.id}`
              : "",
          }}
        >
          <CardContent
            sx={{
              backgroundColor: randomBackgroundColor,
              color: "white",
              textAlign: "center",
              height: 85,
              borderBottomLeftRadius: 5,
              borderBottomRightRadius: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              transition: "transform 0.3s ease, background-color 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "scale(1.02)", // Slight zoom on hover
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontFamily: "basic-sans",
                  fontWeight: "600",
                  lineHeight: {
                    xs: "1.5rem",
                    sm: "1.5rem",
                    md: "1.5rem",
                    lg: "1.75rem",
                  },
                  fontSize: {
                    xs: "1.5rem",
                    sm: "1.5rem",
                    md: "1.05rem",
                    lg: "1.75rem",
                    xl: "1.7rem",
                  }, // Responsive font size
                }}
              >
                {submission.status === "draft"
                  ? "Edit Submission"
                  : "View Submission"}
              </Typography>
              <ForwardRoundedIcon
                sx={{
                  fontSize: {
                    xs: "2rem",
                    sm: "2.4rem",
                    md: "2.8rem",
                    lg: "3.2rem",
                  }, // Responsive size for the icon
                  marginLeft: 1, // Space between text and icon
                }}
              />
            </Box>
          </CardContent>
        </Link>
      </Card>
    </Box>
  );
});

export default SubmissionCard;
